/**
 * Primitive Type Metadata
 *
 * Defines metadata for primitive field types used across the ontology.
 * For numeric types with representations, metadata is derived from the representations.
 * For other types (string, boolean, enum), metadata is defined here.
 */

import { primitiveRepresentations } from './representations';

export interface PrimitiveTypeInfo {
  type: string;
  name: string;
  description: string;
  example: string;
  representationId?: string;
}

// Field type to representation ID mapping
const fieldTypeToRepId: Record<string, string> = {
  number: 'rep.prim.real',
  integer: 'rep.prim.integer',
  natural: 'rep.prim.natural',
  fraction: 'rep.prim.fraction',
  percentage: 'rep.prim.percentage',
};

// Build primitive types from representations where possible
function buildPrimitiveTypes(): PrimitiveTypeInfo[] {
  const types: PrimitiveTypeInfo[] = [];

  // Derive from representations
  for (const [fieldType, repId] of Object.entries(fieldTypeToRepId)) {
    const rep = primitiveRepresentations.find((r) => r.id === repId);
    if (rep) {
      types.push({
        type: fieldType,
        name: rep.name,
        description: rep.description,
        example: rep.example ?? '',
        representationId: rep.id,
      });
    }
  }

  // Add non-numeric types (no representation)
  types.push(
    {
      type: 'string',
      name: 'String',
      description: 'A text value. Can be plain text or formatted.',
      example: '"hello", "convergent"',
    },
    {
      type: 'latex',
      name: 'LaTeX Expression',
      description: 'A mathematical expression in LaTeX format.',
      example: '$x^2 + 2x + 1$',
    },
    {
      type: 'boolean',
      name: 'Boolean',
      description: 'A true/false value. Used for conditions and flags.',
      example: 'true, false',
    },
    {
      type: 'enum',
      name: 'Enumeration',
      description: 'A value from a predefined set of options.',
      example: '"convergent" | "divergent"',
    },
  );

  return types;
}

export const primitiveTypes: PrimitiveTypeInfo[] = buildPrimitiveTypes();

export function getPrimitiveType(type: string): PrimitiveTypeInfo | undefined {
  return primitiveTypes.find((t) => t.type === type);
}
